import { Link } from 'react-router-dom';
import { ArrowRight, Check, Search, Handshake, Phone, DollarSign, TrendingUp, Sparkles, Shield, Clock, Zap } from 'lucide-react';
import { SEO, orgJsonLd, softwareJsonLd, faqJsonLd } from '../components/SEO';

const landingFaqs = [
  { q: 'Is RaiseRoot really an affordable grant search tool?', a: 'Yes. RaiseRoot starts at $19/month vs $200-500/month for legacy tools like Instrumentl or GrantStation. Same-quality grant database, priced for small nonprofits.' },
  { q: 'What does the 7-day free trial include?', a: 'Full access to grant search (25 searches on Starter, 50 on Growth), the Corporate Partnership Builder, deadline calendar, watchlist, and learning hub. No charge for 7 days.' },
  { q: 'How is RaiseRoot different from Instrumentl or GrantStation?', a: 'Same core grant discovery, at 1/10 the price. Plus a built-in Corporate Partnership Builder that helps you find and pitch local companies — something legacy grant tools don\'t offer.' },
  { q: 'Do I need a fundraising consultant to use it?', a: 'No, but we include one 60-minute strategy session per month on the Growth plan ($39/mo). Sessions are also available as add-ons ($55/30min or $100/60min) with no subscription required.' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
      <SEO
        title="Affordable Grant Search Tool for Small Nonprofits"
        description="RaiseRoot is an affordable grant search tool + corporate partnership matcher for small nonprofits. Starting at $19/month. 7-day free trial. No contracts."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [orgJsonLd, softwareJsonLd, faqJsonLd(landingFaqs)],
        }}
      />

      {/* ========== HERO ========== */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A0A0A] leading-[1.05] mb-6">
              Grant search + corporate partners.<br />
              <span className="text-[#00A859]">Without the $200/month price tag.</span>
            </h1>

            <p className="text-lg text-[#5C6B67] leading-relaxed max-w-2xl mb-8">
              Legacy grant databases charge $200-500/month. Corporate partnership tools cost even more.
              RaiseRoot combines both — starting at <strong className="text-[#0A0A0A]">$19/month</strong>. Try free for 7 days.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link to="/register">
                <button className="w-full sm:w-auto bg-[#11302A] text-white px-8 py-4 rounded-full font-medium text-base hover:bg-[#0D241F] hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#11302A]/20 flex items-center justify-center gap-2" data-testid="hero-cta-signup">
                  Start 7-Day Free Trial
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/pricing">
                <button className="w-full sm:w-auto bg-white text-[#11302A] px-8 py-4 rounded-full font-medium text-base border border-[#E2E8E6] hover:bg-[#E5EBE9] transition-all duration-300 flex items-center justify-center gap-2" data-testid="hero-cta-pricing">
                  See Pricing
                </button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5C6B67]">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00A859]" /> No credit card charged for 7 days</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00A859]" /> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00A859]" /> No contracts</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== THE PROBLEM ========== */}
      <section className="py-16 bg-white border-y border-[#E2E8E6]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#00A859] text-sm font-medium tracking-wide uppercase mb-3">Small nonprofits are getting priced out</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] max-w-2xl mx-auto">
              The tools built for large nonprofits are draining small ones.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-[#FAFAFA] border border-[#E2E8E6] rounded-2xl p-6">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A] mb-2">$200-500/month tools</h3>
              <p className="text-sm text-[#5C6B67]">
                Grant databases price like enterprise software. A small nonprofit's whole tech budget disappears in one subscription.
              </p>
            </div>

            <div className="bg-[#FAFAFA] border border-[#E2E8E6] rounded-2xl p-6">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                <Handshake className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A] mb-2">Corporate partners are hidden</h3>
              <p className="text-sm text-[#5C6B67]">
                You know local businesses care about causes — but finding, researching and pitching them takes a dev team most nonprofits don't have.
              </p>
            </div>

            <div className="bg-[#FAFAFA] border border-[#E2E8E6] rounded-2xl p-6">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A] mb-2">Annual contracts, complex tools</h3>
              <p className="text-sm text-[#5C6B67]">
                Long onboarding, dashboards you'll never use, and 12-month lock-ins. You just need to find funding — this month.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TWO CORE TOOLS ========== */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#00A859] text-sm font-medium tracking-wide uppercase mb-3">Two tools. One low price.</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] max-w-2xl mx-auto">
              Everything a small nonprofit needs to find funding.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Grant Search */}
            <div className="bg-white border border-[#E2E8E6] rounded-2xl p-8 hover:border-[#00A859]/50 hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 bg-[#00A859]/10 rounded-xl flex items-center justify-center mb-5">
                <Search className="w-6 h-6 text-[#00A859]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-3">Grant Search</h3>
              <p className="text-[#5C6B67] mb-5">
                Filter by focus area, funding amount, geography, funder type, and 20+ more criteria.
                Save searches. Track deadlines. Never miss a grant.
              </p>
              <ul className="space-y-2">
                {[
                  '25 filtered searches/month (Starter)',
                  '26+ filters — focus area, geography, amount',
                  'Deadline calendar & watchlist',
                  'Grant tracker for applications',
                  'Auto-generated Letters of Intent',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#0A0A0A]">
                    <Check className="w-4 h-4 text-[#00A859] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Corporate Partners */}
            <div className="bg-white border border-[#E2E8E6] rounded-2xl p-8 hover:border-[#00A859]/50 hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 bg-[#11302A]/10 rounded-xl flex items-center justify-center mb-5">
                <Handshake className="w-6 h-6 text-[#11302A]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-3">Corporate Partnership Builder</h3>
              <p className="text-[#5C6B67] mb-5">
                Smart-matched local companies aligned to your mission. Guided pipeline from
                identify → research → pitch → won.
              </p>
              <ul className="space-y-2">
                {[
                  'Smart-matched companies in your area',
                  '6-stage pipeline: Identified → Partnered',
                  'Auto-drafted outreach emails & CSR briefs',
                  'Difficulty & fit indicators',
                  'Next-action prompts on every partner',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#0A0A0A]">
                    <Check className="w-4 h-4 text-[#00A859] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== COMPARISON ========== */}
      <section className="py-20 bg-white border-y border-[#E2E8E6]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[#00A859] text-sm font-medium tracking-wide uppercase mb-3">Fair pricing, no games</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A]">
              Compare the honest way.
            </h2>
          </div>

          <div className="overflow-hidden rounded-2xl border border-[#E2E8E6] bg-[#FAFAFA]">
            <div className="grid grid-cols-3 gap-0">
              <div className="p-5 border-r border-[#E2E8E6]">
                <p className="text-xs uppercase tracking-wider text-[#5C6B67] font-semibold mb-2">Legacy grant tools</p>
                <p className="font-heading text-2xl font-bold text-stone-400 line-through">$200-500</p>
                <p className="text-xs text-[#5C6B67] mt-1">per month + annual contract</p>
              </div>
              <div className="p-5 border-r border-[#E2E8E6]">
                <p className="text-xs uppercase tracking-wider text-[#5C6B67] font-semibold mb-2">Partner research tools</p>
                <p className="font-heading text-2xl font-bold text-stone-400 line-through">$150-400</p>
                <p className="text-xs text-[#5C6B67] mt-1">per month, separate tool</p>
              </div>
              <div className="p-5 bg-[#00A859]/5">
                <p className="text-xs uppercase tracking-wider text-[#00A859] font-semibold mb-2">RaiseRoot Starter</p>
                <p className="font-heading text-2xl font-bold text-[#00A859]">$19</p>
                <p className="text-xs text-[#5C6B67] mt-1">both tools, no contract, 7-day trial</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4 mt-8">
            {[
              { icon: Check, label: 'No annual contract' },
              { icon: Check, label: 'Cancel anytime' },
              { icon: Check, label: '7-day free trial' },
              { icon: Check, label: 'Priced honestly' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                <Icon className="w-4 h-4 text-[#00A859] flex-shrink-0" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== STRATEGY SESSIONS ========== */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#11302A] to-[#0D241F] rounded-3xl p-10 md:p-14 text-white relative overflow-hidden">
            <div className="max-w-2xl relative z-10">
              <p className="text-[#00A859] text-sm font-medium tracking-wide uppercase mb-3">Stuck? Get 1:1 help.</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                Book a strategy session — no subscription required.
              </h2>
              <p className="text-white/70 text-lg mb-8">
                Get 30 or 60 minutes with an experienced fundraising strategist. Proposal review, grant strategy,
                corporate outreach — bring your challenge, leave with next steps.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/60">30-Minute Strategy Session</p>
                    <p className="font-heading text-2xl font-bold">$55</p>
                  </div>
                  <Clock className="w-5 h-5 text-white/40" />
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/60">60-Minute Strategy Session</p>
                    <p className="font-heading text-2xl font-bold">$100</p>
                  </div>
                  <Clock className="w-5 h-5 text-white/40" />
                </div>
              </div>
              <Link to="/consulting">
                <button className="bg-white text-[#11302A] px-7 py-3.5 rounded-full font-medium hover:bg-white/90 transition-all flex items-center gap-2" data-testid="strategy-cta">
                  <Phone className="w-4 h-4" /> Book a Session
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 bg-white border-t border-[#E2E8E6]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Sparkles className="w-10 h-10 text-[#00A859] mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-4">
            Try it free for 7 days.
          </h2>
          <p className="text-[#5C6B67] text-lg mb-8">
            No credit card charged until day 8. Cancel anytime. If it doesn't help you find funding, don't pay.
          </p>
          <Link to="/register">
            <button className="bg-[#00A859] text-white px-10 py-4 rounded-full font-medium text-base hover:bg-[#008A4A] hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#00A859]/30 flex items-center justify-center gap-2 mx-auto" data-testid="final-cta">
              Start 7-Day Free Trial
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <p className="text-xs text-[#5C6B67] mt-4">Starter $19/mo · Growth $39/mo · Cancel anytime</p>
        </div>
      </section>
    </div>
  );
}
